import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { line, Color, LIST_PAGES, Size, isEmailAddress, pageKindLineText, resolvedStatusPill, t, type AwardRfqRequest, type BannerFact, type EditRfqRequest, type BannerPill, type ComparisonResponse, type BomCostChangeAsk, type BomCostChangeReply, type RecordResponseRequest, type RfqComparisonAnswer, type SuggestionRow } from '@bms2/shared';
import { Button, Checkbox, DatePicker, Dropdown, NumericInput, Pill, Select, TextInput, Textarea } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { BannerPills } from './BannerPills';
import { BomCostChangeModal, costChangeAskOf } from './BomCostChange';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentViewPage } from './DocumentViewPage';
import { PURCHASE_ORDERS_PATH, apiFetch, patch, post, useApi, DEALS_PATH } from './api';
import styles from './RfqComparisonPage.module.css';
import { Money } from '../../components/Money';
import { useAddNew } from './AddNewHost';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { useDayText } from '../../shell/viewerDates';
import { useStatusResolver } from '../../shell/StatusResolver';

/** 🔨 Relative to the API's own root, never prefixed with /api. */
export const RFQS_PATH = '/rfqs';
/**
 * One request's own route: the page's read and its Edit (decision 1054). 🔒 The
 * read names the vendor the sheet is drawn for where the picker chose one, and
 * names none for the first asked, so the address stays what it was (decision 1513).
 */
export const rfqPath = (rfqId: string, vendor: string | null = null) => `${RFQS_PATH}/${rfqId}${vendorQuery(vendor)}`;

/** 🔒 The `vendor` query the RFQ's sheet routes read, or nothing for the first vendor asked (decision 1513). */
export function vendorQuery(vendor: string | null): string {
  return vendor ? `?vendor=${encodeURIComponent(vendor)}` : '';
}

const USD = 'USD';

/** The vendor's state as a pill: silent is a wait, answered good, declined a refusal, expired inactive. */
export function responsePill(status: string): BannerPill {
  if (status === 'Received') return { text: t('rfq.page.received'), group: 'good' };
  if (status === 'Declined') return { text: t('rfq.page.declined'), group: 'error' };
  if (status === 'Expired') return { text: t('rfq.page.expired'), group: 'inactive' };
  return { text: t('rfq.page.awaiting'), group: 'warning' };
}

type OpenModal = { kind: 'respond'; response: ComparisonResponse } | { kind: 'award'; response: ComparisonResponse } | { kind: 'addVendor' } | { kind: 'edit' } | null;

/**
 * The RFQ's comparison screen (piece 22; decision 0121): every line against
 * every vendor asked, the lowest price on each row marked, each vendor's
 * state, quote number and expiry, and the four acts — record a response by
 * hand until Vendor Response Matching lands, add a vendor, use a response for
 * BOM pricing, and award, which raises the Purchase Order and closes the RFQ.
 * Comparing is the job, so the matrix is the page.
 */
export function RfqComparisonPage({ rfqId, apiBaseUrl = '' }: { rfqId: string; apiBaseUrl?: string }) {
  const dayText = useDayText();
  const resolveStatus = useStatusResolver();
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  /*
   * 🔒 One read answers the whole page (decision 0613): the comparison, the
   * sheet's own projection and the layout it prints on. 💰 The projection was
   * read on a second route for a day, and a page gated on the first read alone
   * opened on whichever answer landed first — found in review, 2026-09-21, where
   * the sheet arriving second left the page on Details with no way back.
   */
  /*
   * 🔒 The vendor the sheet and its download are drawn for (decision 1513): null
   * is the first asked, which the server draws when no vendor is named. Choosing
   * another reads the page again for that vendor, and the answer drawn last stays
   * on screen until it lands, so the page does not blank between the two.
   */
  const [chosenVendor, setChosenVendor] = useState<{ rfqId: string; vendor: string | null }>({ rfqId, vendor: null });
  // 🔨 A choice is the request's own: another request opens on its first vendor.
  const sheetVendor = chosenVendor.rfqId === rfqId ? chosenVendor.vendor : null;
  const setSheetVendor = (vendor: string | null) => setChosenVendor({ rfqId, vendor });
  const page = useApi<RfqComparisonAnswer>(rfqPath(rfqId, sheetVendor), apiBaseUrl);
  const [drawn, setDrawn] = useState<RfqComparisonAnswer | null>(null);
  if (page.state.kind === 'ready' && page.state.data !== drawn) setDrawn(page.state.data);
  const state = page.state.kind === 'loading' && drawn !== null && drawn.rfqId === rfqId ? { kind: 'ready' as const, data: drawn } : page.state;
  const [modal, setModal] = useState<OpenModal>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** 🔒 The costed-change confirmation Use for BOM pricing opens once a drawn Quote went out (decision 1466; T3391); unmounted when closed. */
  const [pricingAsk, setPricingAsk] = useState<{ ask: BomCostChangeAsk; response: ComparisonResponse } | null>(null);
  const [busy, setBusy] = useState(false);
  // The response modal's drafts, keyed by RFQ line.
  const [prices, setPrices] = useState<Record<string, number | null>>({});
  const [leads, setLeads] = useState<Record<string, number | null>>({});
  const [minimums, setMinimums] = useState<Record<string, number | null>>({});
  const [quoteNumber, setQuoteNumber] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');
  const [declined, setDeclined] = useState(false);
  // The award's Ship To, and the added vendor.
  const [shipToSiteId, setShipToSiteId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorEmail, setVendorEmail] = useState('');
  // Edit's drafts: the three things set at issue (decision 1054).
  const [dueOn, setDueOn] = useState('');
  const [chaseEvery, setChaseEvery] = useState<number | null>(null);
  const [alarmAfter, setAlarmAfter] = useState<number | null>(null);

  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const call = async <T,>(path: string, body: unknown): Promise<T | null> => {
    setBusy(true);
    const answer = await post<T>(`${RFQS_PATH}/${rfqId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setNotice(answer.message);
      return null;
    }
    return answer.data;
  };

  const openRespond = (response: ComparisonResponse) => {
    // The currency input reads and writes integer cents, the shape a Currency field keeps (piece 2).
    setPrices(Object.fromEntries(response.lines.map((l) => [l.rfqLineId, l.unitPriceCents])));
    setLeads(Object.fromEntries(response.lines.map((l) => [l.rfqLineId, l.leadTimeDays])));
    setMinimums(Object.fromEntries(response.lines.map((l) => [l.rfqLineId, l.minimumQuantity])));
    setQuoteNumber(response.vendorQuoteNumber ?? '');
    setExpiresOn(response.expiresAt ?? '');
    setNotes(response.notes ?? '');
    setDeclined(response.status === 'Declined');
    setModal({ kind: 'respond', response });
  };

  const saveResponse = async (response: ComparisonResponse) => {
    const lines = Object.entries(prices).flatMap(([rfqLineId, cents]) => (cents !== null && cents >= 0 ? [{ rfqLineId, unitPriceCents: Math.round(cents), leadTimeDays: leads[rfqLineId] ?? null, minimumQuantity: minimums[rfqLineId] ?? null }] : []));
    if (!declined && lines.length === 0) return setNotice(t('rfq.response.needsLine'));
    const body: RecordResponseRequest = { responseId: response.responseId, declined, vendorQuoteNumber: quoteNumber.trim() || null, expiresOn: expiresOn || null, notes: notes.trim() || null, lines };
    if (await call<RfqComparisonAnswer>('responses', body)) {
      setModal(null);
      page.reload();
    }
  };

  /**
   * 🔒 The first press answers the cost rule's ask and opens the list's own confirmation; the press that follows carries Yes, or a holder's typed
   * reason, and writes the price (decision 1466; T3391).
   */
  const applyToBom = async (response: ComparisonResponse, reply: BomCostChangeReply | null = null) => {
    setBusy(true);
    const answer = await post<{ updated: number }>(`${RFQS_PATH}/${rfqId}/use-for-bom-pricing`, reply ? { responseId: response.responseId, costChange: reply } : { responseId: response.responseId }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      const ask = answer.status === 409 ? costChangeAskOf(answer.body) : null;
      if (ask) return setPricingAsk({ ask, response });
      setPricingAsk(null);
      return setNotice(answer.message);
    }
    setPricingAsk(null);
    setNotice(line(t('rfq.page.bomUpdated'), String(answer.data.updated)));
  };

  const award = async (response: ComparisonResponse) => {
    if (!shipToSiteId) return setNotice(t('rfq.award.needsSite'));
    const body: AwardRfqRequest = { responseId: response.responseId, shipToSiteId, fulfillmentMethod: 'Stock' };
    const answer = await call<{ purchaseOrderId: string; purchaseOrderNumber: string }>('award', body);
    if (answer) navigate(`${PURCHASE_ORDERS_PATH}/${answer.purchaseOrderId}`);
  };

  const openEdit = (rfq: RfqComparisonAnswer) => {
    setDueOn(rfq.responsesDueOn ?? '');
    setChaseEvery(rfq.chaseEveryWorkingDays);
    setAlarmAfter(rfq.noResponseAlarmWorkingDays);
    setModal({ kind: 'edit' });
  };

  /** 🔒 The due day, the chase and the alarm, written on the request's own route (decision 1054); the server refuses a closed request. */
  const saveEdit = async () => {
    setBusy(true);
    const body: EditRfqRequest = { responsesDueOn: dueOn || null, chaseEveryWorkingDays: chaseEvery, noResponseAlarmWorkingDays: alarmAfter };
    const answer = await patch<RfqComparisonAnswer>(rfqPath(rfqId), body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setNotice(answer.message);
    setModal(null);
    page.reload();
  };

  const vendorSearch = async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PURCHASE_ORDERS_PATH}/vendors?q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; displayName: string }[] };
    return body.rows.map((row) => ({ id: row.id, line1: row.displayName }));
  };

  const addVendor = async () => {
    if (!vendorId) return setNotice(t('deal.rfq.needsVendor'));
    if (!isEmailAddress(vendorEmail)) return setNotice(t('deal.rfq.badAddress'));
    if (await call('vendors', { vendorCompanyId: vendorId, email: vendorEmail.trim() })) {
      setModal(null);
      setVendorId('');
      setVendorName('');
      setVendorEmail('');
      page.reload();
    }
  };

  return (
    <>
    <AsyncStatePanel state={state} emptyAction={t('rfq.page.loadFailed')} onRetry={page.reload}>
      {(rfq) => {
        const open = rfq.status === 'Issued' || rfq.status === 'ResponsesIn';
        // 🔒 Edit is offered until the request is awarded or cancelled, which is where the server stops taking it (decision 1054).
        const editable = rfq.status !== 'Awarded' && rfq.status !== 'Cancelled';
        const facts: BannerFact[] = [
          { label: t('rfq.page.deal'), value: rfq.dealName, route: rfq.dealId ? `${DEALS_PATH}/${rfq.dealId}` : null },
          { label: t('rfq.page.issued'), value: rfq.issuedAt ? dayText(rfq.issuedAt) : null },
          // The due day as the vendor was told it, in the Organization's zone, where the answer carries it (decision 1054).
          { label: t('rfq.page.due'), value: rfq.responsesDueOn ?? (rfq.responsesDueAt ? dayText(rfq.responsesDueAt) : null) },
          { label: t('rfq.page.chase'), value: line(String(rfq.chaseEveryWorkingDays ?? t('rfq.page.default')), t('rfq.page.workingDays')) },
          { label: t('rfq.page.alarm'), value: line(String(rfq.noResponseAlarmWorkingDays ?? t('rfq.page.default')), t('rfq.page.workingDays')) },
          { label: t('rfq.page.awarded'), value: rfq.awardedPurchaseOrder?.purchaseOrderNumber ?? null, route: rfq.awardedPurchaseOrder ? `${PURCHASE_ORDERS_PATH}/${rfq.awardedPurchaseOrder.id}` : null },
        ];
        // 🔨 The request's own pill reads the status table, never a colour typed here (T2093): a Draft is grey.
        const statusPill: BannerPill = resolvedStatusPill(resolveStatus, 'RFQStatus', rfq.status);
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(rfq);
        const notesPanel = <DocumentNotes recordType="RFQ" recordId={rfqId} recordLabel={rfq.rfqNumber} apiBaseUrl={apiBaseUrl} onError={setNotice} carried={carried} />;
        // 🔒 The files attached to this RFQ, one block placed in the Details view (decision 0457; T1071).
        const filesPanel = <DocumentFilesSection recordType="RFQ" recordId={rfqId} writeRefusal={rfq.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} />;
        return (
          /*
           * 🔒 The RFQ reads as every other accounting document reads (decision
           * 0613; T151): the sheet first, the comparison behind Details. The
           * product owner: *"RFQ does not let you select and view the document or
           * even preview it"*, and earlier, *"treat them like rest of the
           * accounting documents"*.
           */
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            documentType="RFQ"
            icon="accounting"
            title={rfq.rfqNumber}
            subtitle={pageKindLineText({ page: 'document', documentType: 'RFQ' })}
            information={facts}
            status={<BannerPills pills={[statusPill]} attribute="data-rfq-pills" />}
            pageAttributes={{ 'data-rfq-comparison': rfq.rfqId, 'data-rfq-status': rfq.status }}
            print={rfq.printDocument ?? null}
            /* 🔒 Every vendor asked is a party, with its billing address and its response pill (T442; decision 1120). */
            parties={rfq.responses.map((r) => {
              const pill = responsePill(r.status);
              return {
                key: r.vendorCompanyId,
                heading: t('document.print.vendor'),
                name: r.vendorName,
                lines: r.vendorLines ?? [],
                aside: <Pill text={pill.text} foreground={`var(--status-${pill.group}-text)`} background={`var(--status-${pill.group}-bg)`} size={Size.Small} />,
              };
            })}
            partiesHeading={t('rfq.page.vendors')}
            layout={rfq.printLayout ?? null}
            sheet={rfq.printSheet ?? null}
            writeRefusal={rfq.writeRefusal ?? null}
            ribbon={rfq.status}
            pdfPath={`${RFQS_PATH}/${rfqId}/pdf${vendorQuery(sheetVendor)}`}
            /*
             * 🔒 Every vendor receives a request naming no other vendor, so the sheet
             * names one; the picker chooses which, in the order they were added, and is
             * not drawn where one vendor was asked (decision 1513).
             */
            sheetHeader={rfq.responses.length > 1 ? (
              <Select
                size={Size.Medium}
                label={t('document.print.vendor')}
                value={sheetVendor ?? rfq.responses[0]!.vendorCompanyId}
                onChange={(next) => setSheetVendor(next === rfq.responses[0]!.vendorCompanyId ? null : next)}
                options={rfq.responses.map((r) => ({ value: r.vendorCompanyId, label: r.vendorName }))}
                dataAttribute={{ 'data-rfq-sheet-vendor': sheetVendor ?? rfq.responses[0]!.vendorCompanyId }}
              />
            ) : null}
            listRoute={LIST_PAGES.RFQ.route}
            apiBaseUrl={apiBaseUrl}
            notices={notice ? <AlertBanner tone={notice.startsWith(t('rfq.page.bomUpdated')) ? 'good' : 'error'} message={notice} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
            footer={
              <>
                {open ? <Button size={Size.Medium} color={Color.Navy} text={[t('rfq.page.addVendor')]} actions={[() => setModal({ kind: 'addVendor' })]} disabled={busy} /> : null}
                {/* 🔨 The sheet the vendor fills in stays a download: it is a spreadsheet, and there is no viewer for one. */}
                {/* 🔒 Drawn for the vendor the picker names, as the sheet above is (decision 1513). */}
                <a className={styles.link} href={`${apiBaseUrl}${DEALS_PATH}/rfqs/${rfqId}/xlsx${vendorQuery(sheetVendor)}`} data-rfq-sheet-download="true">{t('rfq.page.sheet')}</a>
              </>
            }
            footerAttributes={{ 'data-rfq-footer': open ? 'open' : 'closed' }}
            // 🔒 Edit at the footer's right reopens the due day, the chase and the alarm (decision 1054); Add vendor and the sheet stay beside it.
            {...(editable ? { onEdit: () => openEdit(rfq) } : {})}
            details={
              <>
              {rfq.responses.length === 0 ? <p className={styles.muted}>{t('rfq.page.noVendors')}</p> : null}
              {/* 🔨 A grid with table roles, not a bare table: every table in the application is the List Viewer's, the Item Table's or the harness's (piece 4). */}
              <div className={styles.scroll}>
                <div className={styles.matrix} role="table" data-rfq-matrix={rfq.responses.length} style={{ gridTemplateColumns: `minmax(0, 2fr) minmax(0, 0.7fr) minmax(0, 0.8fr) repeat(${Math.max(rfq.responses.length, 1)}, minmax(var(--modal-field-min-width), 1fr))` }}>
                  <div role="row" className={styles.row}>
                    <div role="columnheader" className={styles.head}>{t('rfq.page.line')}</div>
                    <div role="columnheader" className={styles.head}>{t('rfq.page.qty')}</div>
                    <div role="columnheader" className={styles.head}>{t('rfq.page.bomPrice')}</div>
                    {rfq.responses.map((r) => {
                      const pill = responsePill(r.status);
                      return (
                        <div key={r.responseId} role="columnheader" className={`${styles.head} ${styles.vendorHead}`} data-rfq-vendor={r.vendorCompanyId} data-rfq-response-status={r.status}>
                          <span className={styles.vendorName}>{r.vendorName}</span>
                          <Pill text={pill.text} foreground={`var(--status-${pill.group}-text)`} background={`var(--status-${pill.group}-bg)`} size={Size.Small} />
                          {r.vendorQuoteNumber ? <span className={styles.small}>{line(t('rfq.page.quote'), r.vendorQuoteNumber, r.expiresAt ? `· ${t('rfq.page.expires')} ${r.expiresAt}` : null)}</span> : null}
                        </div>
                      );
                    })}
                    {rfq.responses.length === 0 ? <div role="columnheader" className={styles.head} /> : null}
                  </div>
                  {rfq.matrix.rows.map((row) => (
                    <div key={row.line.lineId} role="row" className={styles.row} data-rfq-row={row.line.lineId}>
                      <div role="cell" className={styles.cell}>
                        <span className={styles.lineName}>{line(String(row.line.lineNumber), row.line.sku ? `· ${row.line.sku}` : null)}</span>
                        <span className={styles.small}>{row.line.description}</span>
                      </div>
                      <div role="cell" className={`${styles.cell} ${styles.qtyCell}`}>{line(String(row.line.quantity), row.line.unitName)}</div>
                      <div role="cell" className={`${styles.cell} ${styles.qtyCell}`}>{row.line.bomPriceCents === null ? t('rfq.page.noPrice') : <Money cents={row.line.bomPriceCents} currencyCode={USD} />}</div>
                      {rfq.responses.map((r) => {
                        const cell = row.cells[r.responseId] ?? null;
                        return (
                          <div key={r.responseId} role="cell" className={`${styles.cell} ${cell?.lowest ? styles.lowest : ''}`} data-rfq-cell={`${row.line.lineId}:${r.responseId}`} data-rfq-lowest={cell?.lowest ? 'true' : 'false'}>
                            {cell ? (
                              <>
                                <span className={styles.price}><Money cents={cell.unitPriceCents} currencyCode={USD} /></span>
                                <span className={styles.small}>{line(cell.leadTimeDays !== null ? `${cell.leadTimeDays}${t('rfq.page.days')} ${t('rfq.page.lead')}` : null, cell.minimumQuantity !== null ? `${t('rfq.page.min')} ${cell.minimumQuantity}` : null, cell.lowest ? t('rfq.page.lowest') : null)}</span>
                              </>
                            ) : (
                              <span className={styles.muted}>{t('rfq.page.noPrice')}</span>
                            )}
                          </div>
                        );
                      })}
                      {rfq.responses.length === 0 ? <div role="cell" className={styles.cell} /> : null}
                    </div>
                  ))}
                  <div role="row" className={styles.row}>
                    <div role="cell" className={`${styles.cell} ${styles.totalLabel}`}>{t('rfq.page.total')}</div>
                    {rfq.responses.map((r) => {
                      const total = rfq.matrix.totals.find((tt) => tt.responseId === r.responseId)!;
                      return (
                        <div key={r.responseId} role="cell" className={styles.cell} data-rfq-total={r.responseId}>
                          <span className={styles.price}><Money cents={total.totalCents} currencyCode={USD} /></span>
                          <span className={styles.small}>{line(String(total.pricedLines), t('rfq.page.of'), String(rfq.lines.length), t('rfq.page.priced'))}</span>
                        </div>
                      );
                    })}
                    {rfq.responses.length === 0 ? <div role="cell" className={styles.cell} /> : null}
                  </div>
                  <div role="row" className={styles.row}>
                    <div role="cell" className={`${styles.cell} ${styles.totalLabel}`} />
                    {rfq.responses.map((r) => (
                      <div key={r.responseId} role="cell" className={`${styles.cell} ${styles.actions}`} data-rfq-actions={r.responseId}>
                        {open ? <Button size={Size.Small} text={[t('rfq.page.record')]} actions={[() => openRespond(r)]} disabled={busy} /> : null}
                        {r.status === 'Received' ? <Button size={Size.Small} text={[t('rfq.page.useForBom')]} actions={[() => void applyToBom(r)]} disabled={busy || !rfq.dealId} /> : null}
                        {open && r.status === 'Received' ? <Button size={Size.Small} color={Color.Navy} text={[t('rfq.page.award')]} actions={[() => { setShipToSiteId(rfq.receivingSites.length === 1 ? rfq.receivingSites[0]!.id : ''); setModal({ kind: 'award', response: r }); }]} disabled={busy} /> : null}
                      </div>
                    ))}
                    {rfq.responses.length === 0 ? <div role="cell" className={styles.cell} /> : null}
                  </div>
                </div>
              </div>
              {filesPanel}
              {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
              {notesPanel}
              </>
            }
          >
              {pricingAsk ? (
                <BomCostChangeModal
                  ask={pricingAsk.ask}
                  saving={busy}
                  onConfirm={() => void applyToBom(pricingAsk.response, { confirm: true })}
                  onOverride={(reason) => void applyToBom(pricingAsk.response, { overrideReason: reason })}
                  onCancel={() => setPricingAsk(null)}
                />
              ) : null}
              {modal?.kind === 'respond' ? (
                <Modal
                  key={`respond-${modal.response.responseId}`}
                  open
                  title={line(t('rfq.response.title'), '·', modal.response.vendorName)}
                  onCancel={() => setModal(null)}
                  dataModal="rfq-response"
                  width="wide"
                  actions={[
                    { kind: 'primary', label: t('rfq.response.save'), onAct: () => void saveResponse(modal.response), disabled: busy },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: () => setModal(null) },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('rfq.response.hint')}</p>
                  <Checkbox label={t('rfq.response.declined')} checked={declined} onChange={setDeclined} name="declined" />
                  {!declined ? (
                    <ul className={styles.responseLines} data-rfq-response-lines={rfq.lines.length}>
                      {rfq.lines.map((l) => (
                        <li key={l.lineId} className={styles.responseLine} data-rfq-response-line={l.lineId}>
                          <span className={styles.lineMain}>
                            <span className={styles.lineName}>{line(String(l.lineNumber), l.sku ? `· ${l.sku}` : null, '·', String(l.quantity), l.unitName)}</span>
                            <span className={styles.small}>{l.description}</span>
                          </span>
                          <NumericInput size={Size.Medium} label={t('rfq.response.unitPrice')} kind="currency" decimals={2} value={prices[l.lineId] ?? null} onChange={(v) => setPrices((was) => ({ ...was, [l.lineId]: v }))} name={`price-${l.lineId}`} />
                          <NumericInput size={Size.Small} label={t('rfq.response.leadTime')} kind="number" decimals={0} value={leads[l.lineId] ?? null} onChange={(v) => setLeads((was) => ({ ...was, [l.lineId]: v }))} name={`lead-${l.lineId}`} />
                          <NumericInput size={Size.Small} label={t('rfq.response.minimum')} kind="number" decimals={2} value={minimums[l.lineId] ?? null} onChange={(v) => setMinimums((was) => ({ ...was, [l.lineId]: v }))} name={`min-${l.lineId}`} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div className={modalStyles.pair}>
                    <TextInput size={Size.Medium} label={t('rfq.response.vendorQuote')} value={quoteNumber} onChange={setQuoteNumber} name="vendorQuoteNumber" />
                    <DatePicker size={Size.Small} label={t('rfq.response.expiresOn')} value={expiresOn} onChange={setExpiresOn} mode="date" name="expiresOn" />
                  </div>
                  <Textarea size={Size.Large} label={t('rfq.response.notes')} value={notes} onChange={setNotes} />
                </Modal>
              ) : null}
              {modal?.kind === 'award' ? (
                <Modal
                  key={`award-${modal.response.responseId}`}
                  open
                  title={line(t('rfq.award.title'), '·', modal.response.vendorName)}
                  onCancel={() => setModal(null)}
                  dataModal="rfq-award"
                  actions={[
                    { kind: 'primary', label: t('rfq.award.save'), onAct: () => void award(modal.response), disabled: busy || !shipToSiteId },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: () => setModal(null) },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('rfq.award.hint')}</p>
                  <Select size={Size.Medium} label={t('rfq.award.shipTo')} required value={shipToSiteId} onChange={setShipToSiteId} placeholder={t('customFieldsModal.none')} options={rfq.receivingSites.map((s) => ({ value: s.id, label: s.label }))} dataAttribute={{ 'data-rfq-award-site': 'true' }} />
                </Modal>
              ) : null}
              {modal?.kind === 'edit' ? (
                <Modal
                  open
                  title={line(t('rfq.edit.title'), '·', rfq.rfqNumber)}
                  onCancel={() => setModal(null)}
                  dataModal="rfq-edit"
                  actions={[
                    { kind: 'primary', label: t('rfq.edit.save'), onAct: () => void saveEdit(), disabled: busy },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: () => setModal(null) },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('rfq.edit.hint')}</p>
                  <div className={modalStyles.pair}>
                    <DatePicker size={Size.Small} label={t('deal.rfq.dueOn')} value={dueOn} onChange={setDueOn} mode="date" name="rfqDueOn" />
                  </div>
                  <div className={modalStyles.pair}>
                    <NumericInput size={Size.Small} label={t('deal.rfq.chaseEvery')} kind="number" decimals={0} value={chaseEvery} onChange={setChaseEvery} name="chaseEveryWorkingDays" />
                    <NumericInput size={Size.Small} label={t('deal.rfq.alarmAfter')} kind="number" decimals={0} value={alarmAfter} onChange={setAlarmAfter} name="noResponseAlarmWorkingDays" />
                  </div>
                  <p className={modalStyles.fieldLabel}>{t('deal.rfq.chaseHint')}</p>
                </Modal>
              ) : null}
              {modal?.kind === 'addVendor' ? (
                <Modal
                  open
                  title={t('rfq.addVendor.title')}
                  onCancel={() => setModal(null)}
                  dataModal="rfq-add-vendor"
                  actions={[
                    { kind: 'primary', label: t('deal.rfq.send'), onAct: () => void addVendor(), disabled: busy },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: () => setModal(null) },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('rfq.addVendor.hint')}</p>
                  <div data-rfq-add-vendor="true">
                    <Dropdown size={Size.Medium} label={t('deal.rfq.vendor')} entityLabel={t('deal.rfq.vendorEntity')} search={vendorSearch} minChars={0} value={vendorName} onSelect={(row) => { setVendorId(row.id); setVendorName(row.line1); }} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, (row) => { setVendorId(row.id); setVendorName(row.line1); })} required />
                  </div>
                  <TextInput size={Size.Medium} label={t('deal.rfq.email')} value={vendorEmail} onChange={setVendorEmail} type="email" required name="rfqVendorEmail" />
                </Modal>
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
    {addNew.host}
    </>
  );
}
